// Things below the board are not drawn.
//
// New ground rises from under the board, and everything standing on it rises
// welded to the tile (`BoardTiles.riseOf`). Until now the whole arrangement was
// simply *visible* on the way up — a camp's army, its scrap and its lanes slid
// up through the floor in plain sight, which reads as the board being
// transparent rather than as ground arriving.
//
// **Height, not bookkeeping.** The obvious implementation is a per-thing
// opacity driven by how far through its rise each thing is, and it is the wrong
// one twice over: the tiles and every industry pool are `InstancedMesh`, which
// has one material for hundreds of instances, and the pieces share a material
// per rank — so "per thing" means either a per-instance attribute threaded
// through fourteen separate draw loops or a material clone per piece. Both are
// a lot of plumbing to express something that is already true of the geometry:
// *this vertex is below the floor*. So the fade is a function of world Y, read
// per vertex, and every material that can rise gets the same three lines.
//
// Three things fall out of doing it per vertex rather than per object, and all
// three are the behaviour you want:
//
//   - A piece **emerges** — its crown clears the floor and is solid while its
//     base is still under, so it materialises through the plane instead of
//     appearing whole.
//   - Instanced and single meshes need no distinction at all.
//   - Nothing has to know *whether* it is rising. A seated thing is above the
//     band and comes out at alpha 1 by arithmetic.
//
// **The band's top is the tile's underside, not zero**, which is the whole of
// what keeps seated things opaque: a tile at rest spans `-TILE_HEIGHT..0`, so
// a band ending at 0 would leave every tile's own side faces slightly
// translucent and every ingot resting near the floor a little see-through. The
// band ends where the lowest seated geometry begins, so at rest every fragment
// on the board is at or above it and the whole effect switches itself off.

import type { Material } from "three";

/**
 * The bottom of the fade: fully invisible at or below this world height.
 *
 * The rise depth, so a thing at the very start of its ascent is at alpha 0
 * exactly. Imported *from* here by `boardTiles.ts` rather than the other way
 * round, because a fade band that did not match the drop would either clip
 * early — a pop — or never reach zero, which is the bug this fixes.
 */
export const RISE_DEPTH = 1.6;

/**
 * The top of the fade: fully opaque at or above this world height.
 *
 * The underside of a seated tile. See the header — this is the number that
 * keeps the effect from touching a board that is not moving.
 */
export const RISE_FADE_TOP = -0.12;

/** What alpha a fragment at this world height gets. Mirrors the GLSL below. */
export function riseAlpha(worldY: number): number {
  const t = (worldY - -RISE_DEPTH) / (RISE_FADE_TOP - -RISE_DEPTH);
  const clamped = Math.min(1, Math.max(0, t));
  // `smoothstep`, so the arrival is soft at both ends rather than a ramp that
  // starts and stops abruptly — the same argument `placeTiles` makes for easing
  // the rise itself.
  return clamped * clamped * (3 - 2 * clamped);
}

const VERTEX_HEAD = `varying float vRiseY;`;

/**
 * Where the vertex ends up in world space.
 *
 * After `project_vertex`, so `transformed` is final — a piece's chevron, a
 * bent belt and a skinned model have all had their say by then. The instancing
 * branch is not optional: without it every instance of a pool would be
 * measured at the *pool's* origin, which is the board's centre, and a whole
 * board of belts would fade in and out together.
 */
const VERTEX_BODY = `
#ifdef USE_INSTANCING
  vRiseY = (modelMatrix * instanceMatrix * vec4(transformed, 1.0)).y;
#else
  vRiseY = (modelMatrix * vec4(transformed, 1.0)).y;
#endif
`;

const FRAGMENT_HEAD = `
varying float vRiseY;
uniform float uRiseTop;
uniform float uRiseBottom;
`;

/**
 * The fade itself, at the very end of the fragment shader.
 *
 * After `dithering_fragment`, which is the last chunk to touch
 * `gl_FragColor` — injected earlier and tone mapping would run on a colour
 * whose alpha had already been spent.
 */
const FRAGMENT_BODY = `
gl_FragColor.a *= smoothstep(uRiseBottom, uRiseTop, vRiseY);
`;

/**
 * Fade this material below the board.
 *
 * **`transparent` is deliberately left alone.** A material flagged transparent
 * is sorted back-to-front and leaves the opaque pass, which is a real change to
 * how a board of several hundred instanced belts draws — and it would be paid
 * on every frame of every game to serve half a second of animation once a
 * stage. `setRising` switches it on for the length of the rise and off again;
 * this only compiles the shader that reads it.
 *
 * Follows `armyTint.ts`: `onBeforeCompile` runs once per program, and
 * `customProgramCacheKey` is what stops a material that has been given this
 * from sharing a compiled program with one that has not.
 */
export function fadeBelowBoard<T extends Material>(material: T): T {
  // **Idempotent, because the funnel it wants to sit in is not.**
  // `PieceViews.applyFade` runs over every material each time the belt tool
  // opens, and composing the hook with itself would inject the varying twice
  // and fail to compile — a black board, a long way from the line that caused
  // it. Cheap to guard and the guard is the honest kind: it says a material
  // already has this rather than trying to detect it.
  const marked = material as T & { riseFaded?: boolean };
  if (marked.riseFaded) return material;
  marked.riseFaded = true;
  const existing = material.onBeforeCompile;
  const existingKey = material.customProgramCacheKey;
  material.onBeforeCompile = (shader, renderer) => {
    // Composed rather than replaced: a piece's material may already carry the
    // army tint or the paint mask, and clobbering that hook would silently
    // return every army to the colour it was authored in.
    existing?.call(material, shader, renderer);
    shader.uniforms.uRiseTop = { value: RISE_FADE_TOP };
    shader.uniforms.uRiseBottom = { value: -RISE_DEPTH };
    shader.vertexShader = `${VERTEX_HEAD}\n${shader.vertexShader}`.replace(
      "#include <project_vertex>",
      `#include <project_vertex>\n${VERTEX_BODY}`
    );
    shader.fragmentShader = `${FRAGMENT_HEAD}\n${shader.fragmentShader}`.replace(
      "#include <dithering_fragment>",
      `#include <dithering_fragment>\n${FRAGMENT_BODY}`
    );
  };
  material.customProgramCacheKey = () =>
    `${existingKey ? existingKey.call(material) : ""}|riseFade`;
  material.needsUpdate = true;
  return material;
}

/**
 * Switch the alpha blending on for the length of a rise, and off after it.
 *
 * The shader multiplies alpha whether or not this is on; what this decides is
 * whether the renderer *honours* it. So a settled board pays nothing — no
 * transparent pass, no back-to-front sort — and the frames that need it get it.
 *
 * `depthWrite` stays **on** throughout, which is not the usual pairing. The
 * usual reason to drop it is that a transparent surface should not occlude what
 * is behind it; here almost every fragment is at alpha 1 and the few that are
 * not are under the floor, so keeping it is what stops the board's own tiles
 * from sorting through each other for half a second.
 *
 * **`transparent` has more than one owner, so this takes the answer rather than
 * the question.** `PieceViews.setFaded` already drives the same flag for the
 * belt tool's ghosting, and two callers each writing their own reason to one
 * boolean is a race whose symptom is the army staying solid under the belt tool
 * because a rise happened to finish. Each view class combines its own reasons
 * and calls this with the result.
 */
export function setTransparent(materials: Iterable<Material>, on: boolean): void {
  for (const material of materials) {
    if (material.transparent === on) continue;
    material.transparent = on;
    material.needsUpdate = true;
  }
}
